import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent
} from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function formatDate(dateString) {
  if (!dateString) return 'Not set';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Invalid date';
  }
}

function formatIndianDate(dateString) {
  if (!dateString) return 'Not set';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    return 'Invalid date';
  }
}

function DetailView({ open, onClose, data, type }) {
  if (!data) return null;

  const renderClientDetails = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Organization Details
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Organization Name"
              secondary={data.organization_name || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="SPOC Name"
              secondary={data.spoc_name || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Address"
              secondary={data.address || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Email"
              secondary={data.email || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Registration Number"
              secondary={data.registration_number || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="GST Number"
              secondary={data.gst_number || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Mobile Number"
              secondary={data.mobile_number || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Type"
              secondary={data.type || 'Not set'}
            />
          </ListItem>
        </List>
      </Grid>
    </Grid>
  );

  const renderVendorDetails = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Organization Details
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Organization Name"
              secondary={data.organization_name || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="SPOC Name"
              secondary={data.spoc_name || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Address"
              secondary={data.address || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Phone"
              secondary={data.phone || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Email"
              secondary={data.email || 'Not set'}
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Type"
              secondary={data.type || 'Not set'}
            />
          </ListItem>
        </List>
      </Grid>
    </Grid>
  );

  const renderProjectDetails = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, color: '#F79233' }}>
              Project Overview
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="PO Number"
                  secondary={data.po_number || 'Not set'}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="PO Date"
                  secondary={formatIndianDate(data.po_date) || 'Not set'}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="PO Party"
                  secondary={data.po_party_name || 'Not set'}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="End Date"
                  secondary={formatIndianDate(data.end_date) || 'Not set'}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mb: 2, color: '#F79233' }}>
          Line Items
        </Typography>
        {data.line_items && data.line_items.map((item, index) => (
          <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              Line Item {index + 1}
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Corridor ID"
                  secondary={item.corridor_id || 'Not set'}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Corridor"
                  secondary={item.corridor || 'Not set'}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Stretch (KM)"
                  secondary={item.stretch_km ? `${item.stretch_km} km` : 'Not set'}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="From Coordinates"
                  secondary={`Lat: ${item.from_lat || 'N/A'}, Lng: ${item.from_lng || 'N/A'}`}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="To Coordinates"
                  secondary={`Lat: ${item.to_lat || 'N/A'}, Lng: ${item.to_lng || 'N/A'}`}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Scope of Work"
                  secondary={
                    <Box>
                      {item.scope_of_work_1 && (
                        <Typography variant="body2">
                          {item.scope_of_work_1}: {item.quantity_1}
                        </Typography>
                      )}
                      {item.scope_of_work_2 && (
                        <Typography variant="body2">
                          {item.scope_of_work_2}: {item.quantity_2}
                        </Typography>
                      )}
                      {item.scope_of_work_3 && (
                        <Typography variant="body2">
                          {item.scope_of_work_3}: {item.quantity_3}
                        </Typography>
                      )}
                      {!item.scope_of_work_1 && !item.scope_of_work_2 && !item.scope_of_work_3 && (
                        <Typography variant="body2">No scope of work defined</Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            </List>
          </Box>
        ))}
      </Grid>
    </Grid>
  );

  const renderContent = () => {
    switch (type) {
      case 'client':
        return renderClientDetails();
      case 'vendor':
        return renderVendorDetails();
      case 'project':
        return renderProjectDetails();
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {type === 'client' ? 'Client Details' :
         type === 'vendor' ? 'Vendor Details' :
         'Project Details'}
      </DialogTitle>
      <DialogContent>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default DetailView; 